import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/context/SessionContext';
import { useCheckout } from '@/context/CheckoutContext';
import CreateStoreForm from '@/components/CreateStoreForm';
import StoreList from '@/components/StoreList';
import { Plus, Store, X } from '@/components/icons';
import { Card, CardContent } from '@/components/ui/card';
import { supabase } from '@/lib/supabase/client';
import { addCartItem } from '@/lib/supabase/carts';
import { addToTempCart } from '@/lib/tempCart';
import type { Product } from '@/lib/supabase/products';

const DEFAULT_MINIBID_STORE_ID = '30e79df7-81e8-4c5f-a825-602e5899d961';
const DEFAULT_MINIBID_STORE_NAME = 'Minibid 24hs';

export default function StoresPage() {
  const [showCreate, setShowCreate] = useState<boolean>(false);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [rentStoreLoading, setRentStoreLoading] = useState(false);
  const navigate = useNavigate();
  const { session } = useSession();
  const { openCheckoutSidebar, setPreferredStoreId, setPreferredStoreName } = useCheckout();

  const handleRentStoreClick = useCallback(async () => {
    if (rentStoreLoading) return;

    setRentStoreLoading(true);
    try {
      const selectColumns = 'id, store_id, title, description, fixed_sale_price, fixed_rental_price, stock, main_image_url, cover_url, image_urls, details';
      const candidateStoreIds = Array.from(new Set([DEFAULT_MINIBID_STORE_ID].filter(Boolean)));

      const scoreRentalCandidate = (product: Product): number => {
        const details = (product.details && typeof product.details === 'object') ? product.details : {};
        const title = String(product.title || '').toLowerCase();
        const description = String(product.description || '').toLowerCase();
        const rentalTarget = String((details as any).rental_target || '').toLowerCase();
        const billingMode = String((details as any).billing_mode || '').toLowerCase();

        let score = 0;
        if ((details as any).is_store_rental === true) score += 10;
        if (billingMode === 'per_day') score += 5;
        if (rentalTarget.includes('store') || rentalTarget.includes('tienda')) score += 5;
        if (title.includes('alquilar tienda') || title.includes('alquiler tienda')) score += 4;
        if (title.includes('rental')) score += 3;
        if (description.includes('tienda') || description.includes('store')) score += 2;
        return score;
      };

      let rentalProduct: Product | null = null;

      if (candidateStoreIds.length > 0) {
        const localCandidatesQuery = await supabase
          .from('products')
          .select(selectColumns)
          .in('store_id', candidateStoreIds)
          .eq('status', 'available')
          .limit(200);

        const localCandidates = (localCandidatesQuery.data as Product[] | null) || [];
        const bestLocal = localCandidates
          .map((item) => ({ item, score: scoreRentalCandidate(item) }))
          .sort((a, b) => b.score - a.score)[0];

        if (bestLocal && bestLocal.score > 0) {
          rentalProduct = bestLocal.item;
        }
      }

      if (!rentalProduct) {
        const globalFallbackQuery = await supabase
          .from('products')
          .select(selectColumns)
          .eq('status', 'available')
          .or('title.ilike.%alquilar%,title.ilike.%alquiler%,title.ilike.%rental%,description.ilike.%tienda%')
          .limit(200);

        const globalCandidates = (globalFallbackQuery.data as Product[] | null) || [];
        const bestGlobal = globalCandidates
          .map((item) => ({ item, score: scoreRentalCandidate(item) }))
          .sort((a, b) => b.score - a.score)[0];

        if (bestGlobal && bestGlobal.score > 0) {
          rentalProduct = bestGlobal.item;
        }
      }

      if (!rentalProduct) {
        openCheckoutSidebar({ cartOnly: true, preferredStoreId: DEFAULT_MINIBID_STORE_ID });
        return;
      }

      const unitPrice = Number(rentalProduct.fixed_sale_price ?? rentalProduct.fixed_rental_price ?? 25000) || 25000;
      const normalizedProduct: Product = {
        ...rentalProduct,
        fixed_sale_price: unitPrice,
      };
      const rentalStoreId = normalizedProduct.store_id || DEFAULT_MINIBID_STORE_ID;

      if (session?.user?.id) {
        await addCartItem(session.user.id, normalizedProduct.id, 1);
      } else {
        addToTempCart(normalizedProduct, 1, rentalStoreId);
      }

      setPreferredStoreId(rentalStoreId);
      setPreferredStoreName(rentalStoreId === DEFAULT_MINIBID_STORE_ID ? DEFAULT_MINIBID_STORE_NAME : 'Tienda');
      openCheckoutSidebar({ cartOnly: true, preferredStoreId: rentalStoreId });
      window.dispatchEvent(new CustomEvent('cart-updated'));

    } catch (error) {
      console.error('[StoresPage] Error adding store rental product:', error);
    } finally {
      setRentStoreLoading(false);
    }
  }, [openCheckoutSidebar, rentStoreLoading, session?.user?.id, setPreferredStoreId, setPreferredStoreName]);

  return (
    <div className="w-full max-w-6xl mx-auto px-3 md:px-6 py-4 pb-28 md:pb-10">
      {/* Header Section */}
      <div className="mb-8 md:mb-10">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Mis Tiendas</h1>
            <p className="text-base text-muted-foreground mt-2 font-medium">
              Administra tus tiendas y productos
            </p>
          </div>
          {!showCreate && (
            <button 
              onClick={handleRentStoreClick} 
              disabled={rentStoreLoading}
              className="inline-flex items-center justify-center gap-1.5 rounded-full shadow-lg hover:shadow-xl transition-all active:scale-90 duration-75 bg-primary text-primary-foreground h-[43px] px-3 text-xs font-semibold shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
              title={rentStoreLoading ? 'Agregando...' : 'Alquilar nueva tienda'}
            >
              <Plus className="h-5 w-5" />
              <Store className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {showCreate && (
        <Card className="mb-8 border-primary/20 shadow-md bg-card/95 backdrop-blur">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold">Crear nueva tienda</h2>
              <button 
                onClick={() => setShowCreate(false)}
                className="inline-flex items-center justify-center h-8 w-8 rounded-full hover:bg-muted transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <CreateStoreForm onCreated={(s)=>{ setShowCreate(false); if (s?.slug) navigate(`/${s.slug}/tablero`); }} />
          </CardContent>
        </Card>
      )}

      {/* Pull to refresh area */}
      <div
        onTouchStart={(e) => {
          try {
            const t = e.touches[0];
            (window as any).__pullStartY = t.clientY;
            (window as any).__pullDistance = 0;
          } catch (_) {}
        }}
        onTouchMove={(e) => {
          try {
            const startY = (window as any).__pullStartY || 0;
            const t = e.touches[0];
            const dy = t.clientY - startY;
            (window as any).__pullDistance = dy;
            if (window.scrollY === 0 && dy > 0) {
              if (dy > 150) (window as any).__pullDistance = 150;
            }
          } catch (_) {}
        }}
        onTouchEnd={() => {
          try {
            const dy = (window as any).__pullDistance || 0;
            const THRESHOLD = 60;
            if (window.scrollY === 0 && dy >= THRESHOLD) {
              setRefreshKey((k) => k + 1);
            }
          } catch (_) {}
          finally {
            (window as any).__pullStartY = undefined;
            (window as any).__pullDistance = 0;
          }
        }}
      >
        <StoreList 
          refreshKey={refreshKey} 
          onSelect={(s)=>{ if (s?.store_slug || s?.slug) navigate(`/${s.store_slug || s.slug}/tablero`); }}
          onCreateClick={() => setShowCreate(true)}
          showCreateButton={!showCreate}
        />
      </div>
    </div>
  );
}
